import { makeAutoObservable, runInAction } from 'mobx';

import { axiosClient } from '../config/axios-client';
import { AuthenticationPrincipal } from '../model/AuthenticationPrincipal';
import { LoginProps } from '../pages/Login/Login.types';
import { createBasicAuthToken } from '../utils/auth.utils';
import commonStore from './CommonStore';
import localStorageStore from './LocalStorageStore';
import playerColorStore from './PlayerColorStore';
import pushNotificationStore from './PushNotificationStore';
import { StateStore } from './StateStore';
import translateStore from './TranslateStore';
import userSettingsStore from './UserSettingsStore';

class AuthStore implements StateStore {
  public authenticatedUser: AuthenticationPrincipal;

  constructor() {
    makeAutoObservable(this);
  }

  public resetState = (): void => {
    this.authenticatedUser = undefined;
  };

  public login = async ({
    username,
    password,
  }: LoginProps): Promise<AuthenticationPrincipal> => {
    const token = createBasicAuthToken(username, password);

    return axiosClient
      .post<AuthenticationPrincipal>(`/auth`, { username, password })
      .catch(error => {
        localStorageStore.removeToken();
        this.resetState();
        return Promise.reject(error);
      })
      .then(response => {
        localStorageStore.addToken(token);
        runInAction(() => {
          this.authenticatedUser = response.data;
        });
        userSettingsStore.initUserSettings();
        playerColorStore.initPlayersColors();
        pushNotificationStore.updatePushSubscription();
        translateStore.initAvailableLanguages();
        return this.authenticatedUser;
      });
  };

  public logout = (): Promise<void> =>
    axiosClient.post(`/auth/logout`).then(() => {
      localStorageStore.removeToken();
      this.resetState();
      commonStore.clearAllTableData();
    });

  public authenticate = (): Promise<AuthenticationPrincipal> => {
    if (localStorageStore.getToken() != null) {
      const [username, password] = [
        localStorageStore.getLoggedUsername(),
        localStorageStore.getLoggedPassword(),
      ];
      return this.login({ username, password });
    }
    return Promise.reject(new Error('User is not authenticated'));
  };
}

const authStore = new AuthStore();
export default authStore;
